import type { HTMLAttributes, ReactNode, RefObject } from 'react';
import { clsx } from '../clsx';
import { RequiredMark } from './RequiredMark';
import styles from './FieldFrame.module.css';

export interface FieldFrameProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Id of the control inside, for the label. */
  htmlFor: string;
  label?: string | undefined;
  /** Keeps the label for screen readers only (search fields, table filters). */
  labelHidden?: boolean | undefined;
  required?: boolean | undefined;
  /** Adds " optional" after the label (Kontakte 04 "Tippgeber optional"). */
  optional?: boolean | undefined;
  /** Right-aligned note in the label row ("Login-Adresse"). */
  labelEnd?: ReactNode | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  hintId: string;
  errorId: string;
  invalid?: boolean | undefined;
  readOnly?: boolean | undefined;
  disabled?: boolean | undefined;
  leading?: ReactNode | undefined;
  trailing?: ReactNode | undefined;
  /** Top-aligns the adornments (textarea). */
  multiline?: boolean | undefined;
  /** `default` 40 px / 10 12 / 13 px; `compact` 7 10 / 12.5 px (settings dropdowns, popover date fields). */
  size?: 'default' | 'compact' | undefined;
  /** `card` white control; `sunken` the `--surface-sunken` control of popovers and the topbar search. */
  tone?: 'card' | 'sunken' | undefined;
  controlClassName?: string | undefined;
  /** Ref to the control frame (anchor for listboxes and popovers). */
  controlRef?: RefObject<HTMLDivElement | null> | undefined;
  children: ReactNode;
}

/** Label + control frame + hint/error of every form field; the control itself is passed as children. */
export function FieldFrame({
  htmlFor,
  label,
  labelHidden = false,
  required,
  optional,
  labelEnd,
  hint,
  error,
  hintId,
  errorId,
  invalid,
  readOnly,
  disabled,
  leading,
  trailing,
  multiline = false,
  size = 'default',
  tone = 'card',
  controlClassName,
  controlRef,
  className,
  children,
  ...rest
}: FieldFrameProps) {
  return (
    <div className={clsx(styles.wrapper, disabled && styles.disabled, className)} {...rest}>
      {label ? (
        <div className={clsx(styles.labelRow, labelHidden && styles.srOnly)}>
          <label htmlFor={htmlFor} className={styles.label}>
            {label}
            {required ? <RequiredMark /> : null}
            {optional ? <span className={styles.optional}> optional</span> : null}
          </label>
          {labelEnd ? <span className={styles.labelEnd}>{labelEnd}</span> : null}
        </div>
      ) : null}
      <div
        ref={controlRef}
        className={clsx(
          styles.control,
          'field-focus',
          size === 'compact' && styles.compact,
          tone === 'sunken' && styles.sunken,
          multiline && styles.multiline,
          (error || invalid) && styles.invalid,
          readOnly && styles.readOnly,
          controlClassName,
        )}
      >
        {leading ? <span className={styles.adornment}>{leading}</span> : null}
        {children}
        {trailing ? <span className={styles.adornment}>{trailing}</span> : null}
      </div>
      {error ? (
        <div id={errorId} className={styles.error} role="alert">
          {error}
        </div>
      ) : hint ? (
        <div id={hintId} className={styles.hint}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}
